import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";
import { clearDirectoryCache } from "./directory";
import { resolveActiveQueryParam } from "../utils/listActiveFilter";

export type GearType = "manual" | "automatic";

export type Vehicle = {
  id: string;
  tenant_id: string;
  branch_id: string;
  plate_no: string;
  brand_model: string | null;
  gear_type: GearType | null;
  current_km: number;
  last_service_date: string | null;
  next_service_km: number | null;
  next_service_date: string | null;
  insurance_expiry_date: string | null;
  inspection_expiry_date: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type VehicleListResponse = {
  items: Vehicle[];
  total: number;
  limit: number;
  offset: number;
};

export type VehicleInput = {
  branchId?: string;
  plateNo: string;
  brandModel?: string | null;
  gearType?: GearType | null;
  currentKm?: number;
  lastServiceDate?: string | null;
  nextServiceKm?: number | null;
  nextServiceDate?: string | null;
  insuranceExpiryDate?: string | null;
  inspectionExpiryDate?: string | null;
  isActive?: boolean;
  notes?: string | null;
};

export type VehicleListSortField =
  | "plate_no"
  | "brand_model"
  | "current_km"
  | "next_service_km"
  | "insurance_expiry_date"
  | "inspection_expiry_date"
  | "is_active";

export type VehicleListSortDir = "asc" | "desc";

export type VehicleListFilter = {
  q?: string;
  isActive?: boolean | "";
  gearType?: GearType | "";
  branchId?: string;
  sort?: VehicleListSortField;
  order?: VehicleListSortDir;
  limit?: number;
  offset?: number;
};

export type VehicleListState = {
  data: VehicleListResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useVehicleList(filter: VehicleListFilter): VehicleListState {
  const [data, setData] = useState<VehicleListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { q, isActive, gearType, branchId, sort, order, limit, offset } = filter;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<VehicleListResponse>(
        "/vehicles",
        {
          q: q || undefined,
          is_active: resolveActiveQueryParam(isActive),
          gear_type: gearType || undefined,
          branch_id: branchId || undefined,
          sort: sort || undefined,
          order: order || undefined,
          limit: limit ?? 50,
          offset: offset ?? 0,
        },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Araçlar Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q, isActive, gearType, branchId, sort, order, limit, offset, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, reload };
}

export async function createVehicle(input: VehicleInput): Promise<Vehicle> {
  const result = await api.post<Vehicle>("/vehicles", input);
  clearDirectoryCache();
  return result;
}

export async function updateVehicle(id: string, input: VehicleInput): Promise<Vehicle> {
  const result = await api.put<Vehicle>(`/vehicles/${id}`, input);
  clearDirectoryCache();
  return result;
}

export async function setVehicleActive(id: string, isActive: boolean): Promise<Vehicle> {
  const result = await api.post<Vehicle>(`/vehicles/${id}/active`, { isActive });
  clearDirectoryCache();
  return result;
}

export async function updateVehicleKm(
  id: string,
  payload: { currentKm: number; reason?: string | null; appointmentId?: string | null },
): Promise<Vehicle> {
  return api.post<Vehicle>(`/vehicles/${id}/km`, payload);
}

export type DeadlineTone = "ok" | "soon" | "very-soon" | "expired" | "none";

export function deadlineStatus(date: string | null): {
  daysLeft: number | null;
  tone: DeadlineTone;
  label: string;
} {
  if (!date) return { daysLeft: null, tone: "none", label: "Tarih girilmedi" };
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) {
    return { daysLeft: null, tone: "none", label: "Geçersiz tarih" };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { daysLeft: diff, tone: "expired", label: `${Math.abs(diff)} Gün once doldu` };
  if (diff <= 14) return { daysLeft: diff, tone: "very-soon", label: `${diff} Gün Kaldı` };
  if (diff <= 60) return { daysLeft: diff, tone: "soon", label: `${diff} Gün Kaldı` };
  return { daysLeft: diff, tone: "ok", label: `${diff} Gün Kaldı` };
}

export function nextServiceStatus(currentKm: number, nextServiceKm: number | null): {
  remainingKm: number | null;
  tone: DeadlineTone;
  label: string;
} {
  if (nextServiceKm === null || nextServiceKm === undefined) {
    return { remainingKm: null, tone: "none", label: "Servis hedefi yok" };
  }
  const remaining = nextServiceKm - currentKm;
  if (remaining < 0) return { remainingKm: remaining, tone: "expired", label: `${Math.abs(remaining)} km Geçildi` };
  if (remaining <= 200) return { remainingKm: remaining, tone: "very-soon", label: `${remaining} km Kaldı` };
  if (remaining <= 1000) return { remainingKm: remaining, tone: "soon", label: `${remaining} km Kaldı` };
  return { remainingKm: remaining, tone: "ok", label: `${remaining} km Kaldı` };
}

/**
 * Bir sonraki bakim tarihine kalan gun sayisi + tone.
 * `deadlineStatus` ile ayni esikleri kullanir (14 / 60 gun) ama
 * "Bakim hedefi yok" mesajini doner.
 */
export function nextServiceDateStatus(date: string | null): {
  daysLeft: number | null;
  tone: DeadlineTone;
  label: string;
} {
  if (!date) return { daysLeft: null, tone: "none", label: "Bakım hedefi yok" };
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) {
    return { daysLeft: null, tone: "none", label: "Geçersiz tarih" };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { daysLeft: diff, tone: "expired", label: `${Math.abs(diff)} Gün gecikti` };
  if (diff === 0) return { daysLeft: 0, tone: "very-soon", label: "Bugün" };
  if (diff <= 14) return { daysLeft: diff, tone: "very-soon", label: `${diff} Gün Kaldı` };
  if (diff <= 60) return { daysLeft: diff, tone: "soon", label: `${diff} Gün Kaldı` };
  return { daysLeft: diff, tone: "ok", label: `${diff} Gün Kaldı` };
}
